import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Eye, Trash2, Upload, CheckCircle2, XCircle, Loader2,
  Search, FileText, User, Mail, Phone, MapPin, Briefcase, Calendar,
  TrendingUp, Target, BookOpen, Building2, GraduationCap, Sparkles,
  AlertTriangle, ShieldCheck, X, ChevronDown, BarChart3, Filter,
  CloudUpload, FileCheck, Clock, ArrowLeft, ChevronRight, Award,
  Star, Layers, Info, Users, Link2, Github, Zap, ShieldAlert,
  CheckSquare, Square, Bell, Copy, Inbox,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { ResumeDownloadButtons, ResumeDownloadMenu } from '@/components/resume/ResumeDownload';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import Pagination from '@/components/ui/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NativeSelect } from '@/components/ux/NativeSelect';
import SearchableSelect from '@/components/ux/SearchableSelect';
import { ViewToggle, useViewMode } from '@/components/ux/ViewToggle';
import { cn, formatFileSize } from '@/lib/utils';
import { useAuth, hasPermission } from '@/context/AuthContext';

const PAGE_SIZE = 12;
const LIST_PAGE_SIZE = 30;

/* ── helpers ────────────────────────────────────────────────────── */
const atsTone = (s) => s >= 80 ? { fg: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500', ring: '#10b981', bg: 'bg-emerald-500/10' }
  : s >= 60 ? { fg: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500', ring: '#f59e0b', bg: 'bg-amber-500/10' }
  : { fg: 'text-red-600 dark:text-red-400', bar: 'bg-red-500', ring: '#ef4444', bg: 'bg-red-500/10' };

const STATUS_META = {
  completed:  { label: 'Completed',       cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20' },
  queued:     { label: 'Queued',          cls: 'bg-muted text-muted-foreground border-border' },
  extracting: { label: 'Extracting…',    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20' },
  parsing:    { label: 'Parsing…',       cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' },
  scoring:    { label: 'Scoring…',       cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20' },
  failed:     { label: 'Failed',          cls: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20' },
};

const PROCESSING_STEPS = [
  { key: 'queued',     label: 'Queued' },
  { key: 'extracting', label: 'Extracting text' },
  { key: 'parsing',    label: 'Parsing resume' },
  { key: 'scoring',    label: 'Scoring & analysis' },
];

const POLL_INTERVAL_MS = 2500;
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

/** Origin tabs — HR uploads vs candidates who applied through a public job link. */
const SOURCE_TABS = [
  { key: 'admin_upload', label: 'HR Uploads',      icon: CloudUpload },
  { key: 'self_applied', label: 'Job Applicants',  icon: Users       },
];

/**
 * A public application sits in `queued` until someone asks for it to be
 * evaluated, so it looks idle rather than in-flight. HR uploads never hit this
 * state — they start processing the moment they are uploaded.
 */
function isAwaitingEvaluation(resume) {
  return resume.source === 'self_applied' && resume.status === 'queued';
}

/** ATS bands, aligned to the hiring tiers the scoring pipeline assigns. */
const SCORE_BANDS = {
  strong:  { label: 'Strong · 85-100%', min: 85, max: 100 },
  good:    { label: 'Good · 70-84%',    min: 70, max: 84  },
  partial: { label: 'Partial · 50-69%', min: 50, max: 69  },
  weak:    { label: 'Weak · below 50%', min: 0,  max: 49  },
};

const SORTS = {
  recent:     { label: 'Newest first',     fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest:     { label: 'Oldest first',     fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  scoreDesc:  { label: 'Score: high → low', fn: (a, b) => (b.atsScore ?? -1) - (a.atsScore ?? -1) },
  scoreAsc:   { label: 'Score: low → high', fn: (a, b) => (a.atsScore ?? 999) - (b.atsScore ?? 999) },
  nameAsc:    { label: 'Name A → Z',        fn: (a, b) => resumeDisplayName(a).localeCompare(resumeDisplayName(b)) },
  // Applicants who declared nothing sort last rather than mixing into the top.
  skillDesc:  { label: 'Skill match: high → low',
                fn: (a, b) => (b.skillMatch?.percent ?? -1) - (a.skillMatch?.percent ?? -1) },
};

/** Filters for the declared-skill signal, used to triage before spending credits. */
const SKILL_BANDS = {
  allRequired: { label: 'All required skills', test: m => m && m.requiredTotal > 0 && m.requiredMatched === m.requiredTotal },
  strong:      { label: 'Strong · 80%+',       test: m => m && m.percent >= 80 },
  weak:        { label: 'Weak · below 50%',    test: m => m && m.percent < 50 },
};

/** Best available name — parsed data wins, then the apply form, then the filename. */
function resumeDisplayName(r) {
  return r.parsedData?.name || r.applicantName || r.fileName || '';
}

// Suffixed .v2 deliberately: the first release of this toggle defaulted to cards
// and persisted that on load, so anyone who opened the page already has "card"
// stored. A new key retires those values so the list default actually applies.
const VIEW_STORAGE_KEY = 'echohire.resumes.view.v2';
const MAX_FILES = 5;
const DECISION_META = {
  selected: { label: 'Selected', cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20', dot: 'bg-emerald-500' },
  rejected: { label: 'Rejected', cls: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',     dot: 'bg-red-500'     },
  pending:  { label: 'Pending',  cls: 'bg-muted text-muted-foreground border-border',                         dot: 'bg-muted-foreground' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-muted text-muted-foreground border-border' };
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold', m.cls)}>{m.label}</span>;
}
/** True when ATS score meets company pass threshold (Settings → Interview). */
/**
 * The threshold that applies to this resume.
 *
 * Thresholds are per job description now, falling back to the company setting.
 * Gating on the company number alone would offer Accept on a resume the server
 * then refuses, or hide it on one the server would allow.
 */
/** True only for a plain data object — not a React event, DOM node or array. */
function isPlainObject(v) {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v)
    && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);
}

function thresholdFor(resume, companyThreshold) {
  const own = resume?.templateId?.passThreshold;
  return typeof own === 'number' ? own : companyThreshold;
}

function meetsPassThreshold(resume, companyThreshold) {
  const threshold = thresholdFor(resume, companyThreshold);
  if (threshold == null || threshold === undefined) return true;
  return (resume.atsScore ?? 0) >= threshold;
}

/** Show a plain-language error — never raw API / database messages. */
function friendlyApiError(err, fallback = 'Something went wrong. Please try again.') {
  const msg = err?.data?.message || err?.message || '';
  if (!msg) return fallback;
  if (/E11000|MongoError|ValidationError|CastError|HTTP error|NetworkError|fetch failed|resumeIds must|userId is required/i.test(msg)) {
    return err?.data?.message || fallback;
  }
  return msg;
}

function formatSkippedCandidates(skipped = [], max = 3) {
  if (!skipped.length) return '';
  const preview = skipped.slice(0, max).map(s => `${s.name}: ${s.message}`).join(' · ');
  return skipped.length > max ? `${preview} · and ${skipped.length - max} more` : preview;
}

function DecisionBadge({ status }) {
  const key = status || 'pending';
  const m = DECISION_META[key] || DECISION_META.pending;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold', m.cls)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', m.dot)} />{m.label}
    </span>
  );
}

/* ── ATS ring (SVG donut) ────────────────────────────────────────── */
function AtsRing({ score, size = 56 }) {
  const R = size / 2 - 5, circ = 2 * Math.PI * R, cx = size / 2;
  const { ring, fg } = atsTone(score);
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90 absolute inset-0">
        <circle cx={cx} cy={cx} r={R} fill="none" strokeWidth="5" stroke="currentColor" className="text-muted/40" />
        <circle cx={cx} cy={cx} r={R} fill="none" strokeWidth="5" stroke={ring} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ - (score / 100) * circ}
          style={{ filter: `drop-shadow(0 0 3px ${ring}60)` }} />
      </svg>
      <span className={cn('z-10 text-[11px] font-black tabular-nums', fg)}>{score}%</span>
    </div>
  );
}

/* ── mini progress bar ───────────────────────────────────────────── */
function MiniBar({ label, value }) {
  const { bar, fg } = atsTone(value || 0);
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-muted-foreground">{label}</span>
        <span className={cn('text-[10px] font-semibold tabular-nums', fg)}>{value || 0}%</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-all duration-700', bar)} style={{ width: `${value || 0}%` }} />
      </div>
    </div>
  );
}

/* ── KPI card ────────────────────────────────────────────────────── */
function KpiCard({ title, value, icon: Icon, tone }) {
  const tones = {
    primary:     'bg-primary/10 text-primary',
    success:     'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    warning:     'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    destructive: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tones[tone] || tones.primary)}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        <p className="text-xl font-black text-foreground tabular-nums">{value}</p>
      </div>
    </div>
  );
}

/** How long something has been running, in words. */
function elapsedSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

/**
 * Anything still processing well past the point it should have finished.
 *
 * The queue is in-process, so a restart drops queued work and the resume keeps
 * its status forever. Without this, a job stuck for three weeks looks exactly
 * like one that started a minute ago.
 */
const STALLED_AFTER_MS = 30 * 60 * 1000;
function isStalled(resume) {
  const started = resume.updatedAt || resume.createdAt;
  return Boolean(started) && (Date.now() - new Date(started).getTime()) > STALLED_AFTER_MS;
}

/**
 * Compact horizontal trail of where a resume has got to.
 *
 * Used on both the card and the list row so the state is identifiable at a
 * glance in either view, rather than a bare spinner and a word.
 */
function StatusTrail({ resume, compact = false }) {
  const step    = resume.processingStep || resume.status;
  const idx     = PROCESSING_STEPS.findIndex(s => s.key === step);
  const failed  = resume.status === 'failed';
  const stalled = !failed && isStalled(resume);
  const since   = elapsedSince(resume.updatedAt || resume.createdAt);

  return (
    <div className={cn('space-y-1', compact ? '' : 'px-4 pb-3')}>
      <div className="flex items-center gap-1">
        {PROCESSING_STEPS.map((s, i) => {
          const done   = !failed && i < idx;
          const active = !failed && i === idx;
          return (
            <div key={s.key} className="flex flex-1 items-center gap-1" title={s.label}>
              <span className={cn('h-1.5 flex-1 rounded-full transition-colors',
                failed  ? (i <= idx ? 'bg-destructive/60' : 'bg-muted')
                : done   ? 'bg-emerald-500'
                : active ? (stalled ? 'bg-amber-500' : 'bg-primary animate-pulse')
                         : 'bg-muted')} />
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-1.5 text-[10px]">
        {failed ? (
          <span className="font-medium text-destructive">
            Failed{resume.errorCategory ? ` · ${resume.errorCategory}` : ''}
          </span>
        ) : (
          <span className={cn('font-medium', stalled ? 'text-amber-600 dark:text-amber-500' : 'text-foreground')}>
            {PROCESSING_STEPS[idx]?.label || 'Queued'}
          </span>
        )}
        {since && <span className="text-muted-foreground">· {since}</span>}
        {stalled && !failed && (
          <span className="text-amber-600 dark:text-amber-500" title="Nothing has moved for a while — this may need re-running">
            · stalled
          </span>
        )}
      </div>

      {failed && resume.errorMessage && !compact && (
        <p className="line-clamp-2 pt-0.5 text-[10px] leading-snug text-destructive/90">{resume.errorMessage}</p>
      )}
    </div>
  );
}

/**
 * Collects a name or email that parsing could not read.
 *
 * Both are required to create the candidate's login, so without them the
 * accept button simply failed. Asking is far better than sending someone back
 * to re-upload a resume that is perfectly readable by a human.
 */
function MissingDetailsDialog({ open, resume, fields = [], onCancel, onSubmit, busy }) {
  const needsName  = fields.includes('name');
  const needsEmail = fields.includes('email');
  const [name, setName]   = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr]     = useState('');

  useEffect(() => {
    if (open) { setName(resume?.applicantName || ''); setEmail(resume?.applicantEmail || ''); setErr(''); }
  }, [open, resume]);

  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    if (needsName) {
      if (name.trim().length < 2) { setErr("Enter the candidate's full name."); return; }
      payload.name = name.trim();
    }
    if (needsEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setErr('Enter a valid email address.'); return; }
      payload.email = email.trim();
    }
    setErr('');
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && !busy && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              {needsEmail && needsName ? 'Add the candidate’s details'
                : needsEmail ? 'Add the candidate’s email' : 'Add the candidate’s name'}
            </DialogTitle>
            <DialogDescription>
              We could not read {fields.join(' or ')} from <strong>{resume?.fileName || 'this resume'}</strong>.
              Enter {fields.length > 1 ? 'them' : 'it'} to create the account — {fields.length > 1 ? 'they are' : 'it is'} saved
              to the resume, so you will not be asked again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {needsName && (
              <div className="space-y-1.5">
                <Label htmlFor="md-name">Full name</Label>
                <Input id="md-name" value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Priya Sharma" disabled={busy} autoFocus />
              </div>
            )}
            {needsEmail && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="md-email">Email address</Label>
                  {/* Why it is needed, on hover — not a paragraph everyone reads once */}
                  <span
                    tabIndex={0}
                    role="note"
                    className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-border text-[9px] font-bold text-muted-foreground hover:border-primary hover:text-primary focus:outline-none focus:ring-1 focus:ring-ring"
                    title={'Why an email is required:\n• It is the candidate’s login for the interview portal\n• Their credentials are sent to it\n• Interview invites, reminders and results go to it\n\nWithout it the account cannot be created.'}
                  >
                    ?
                  </span>
                </div>
                <Input id="md-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="e.g. priya@example.com" disabled={busy} autoFocus={!needsName} />
                <p className="text-[11px] text-muted-foreground">
                  Used for their login, credentials and interview emails.
                </p>
              </div>
            )}
            {err && (
              <p className="flex items-start gap-1.5 text-xs text-destructive">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />{err}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {busy ? 'Creating…' : 'Save & accept'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Resume card ─────────────────────────────────────────────────── */
function ResumeCard({ resume, onView, onOpenFile, onSelect, onReject, onDelete, onEvaluate, evaluatingId, selecting, selected, onToggleSelect, bulkMode, passThreshold, canManage }) {
  // Before parsing runs, self-applied resumes only have what the candidate typed
  // on the apply form — fall back to that so the card is never blank.
  const name      = resume.parsedData?.name  || resume.applicantName  || resume.fileName;
  const email     = resume.parsedData?.email || resume.applicantEmail || '';
  const skillsFlat = resume.parsedData?.skillsFlat ||
    (Array.isArray(resume.parsedData?.skills) ? resume.parsedData.skills : []);
  const skills = skillsFlat.slice(0, 4);
  const more   = skillsFlat.length - skills.length;
  const ats    = resume.atsScore;
  const done   = resume.status === 'completed';
  const awaiting   = isAwaitingEvaluation(resume);
  const evaluating = evaluatingId === resume._id;
  // An application parked in `queued` is idle, not in flight — only show the
  // live pipeline UI once evaluation has actually been triggered.
  const processing = !TERMINAL_STATUSES.has(resume.status) && !awaiting;
  const pending = !resume.candidateStatus || resume.candidateStatus === 'pending';
  const canAccept = meetsPassThreshold(resume, passThreshold);
  // Selectable to accept, or to evaluate if that has not happened yet.
  const selectable = (done && pending && canAccept) || awaiting || resume.status === 'failed';
  const initials = name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

  const decBorder = resume.candidateStatus === 'selected' ? 'border-l-emerald-500'
    : resume.candidateStatus === 'rejected' ? 'border-l-red-500'
    : awaiting ? 'border-l-violet-500/60'
    : processing ? 'border-l-primary/40' : 'border-l-transparent';

  return (
    <div className={cn(
      'group relative flex flex-col rounded-xl border border-border bg-card transition-shadow hover:shadow-md overflow-hidden border-l-4',
      decBorder,
      selected && 'ring-2 ring-primary ring-offset-1',
    )}>
      {/* Bulk checkbox */}
      {bulkMode && canManage && selectable && (
        <button type="button"
          onClick={() => onToggleSelect(resume._id)}
          className="absolute top-2 right-2 z-10 text-primary hover:scale-110 transition-transform">
          {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-muted-foreground" />}
        </button>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-black text-primary ring-2 ring-primary/10">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-semibold text-foreground leading-snug">{name}</p>
          {email && <p className="truncate text-[11px] text-muted-foreground">{email}</p>}
          {resume.templateId?.title && (
            <p className="truncate text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
              <Briefcase className="h-2.5 w-2.5 shrink-0" />{resume.templateId.title}
            </p>
          )}
          {/* Duplicate warning */}
          {resume.duplicateOf && (
            <p className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
              <Copy className="h-2.5 w-2.5 shrink-0" />Duplicate User detected
            </p>
          )}
        </div>
        {done && ats != null ? (
          <AtsRing score={ats} size={52} />
        ) : resume.status === 'failed' ? (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-500/10">
            <XCircle className="h-5 w-5 text-destructive" />
          </div>
        ) : awaiting ? (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-500/10"
            title="Not evaluated yet">
            <Sparkles className="h-5 w-5 text-violet-500" />
          </div>
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted/40">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Applicant contact details — the only data available before evaluation */}
      {awaiting && resume.applicantPhone && (
        <p className="flex items-center gap-1 px-4 pb-1.5 text-[11px] text-muted-foreground">
          <Phone className="h-2.5 w-2.5 shrink-0" />{resume.applicantPhone}
        </p>
      )}
      {resume.skillMatch?.totalSkills > 0 && (
        <div className="px-4 pb-3">
          <SkillMatchBadge match={resume.skillMatch} />
        </div>
      )}

      {/* Processing steps OR error snippet OR skills */}
      {processing ? (
        <StatusTrail resume={resume} />
      ) : resume.status === 'failed' ? (
        <div className="mx-4 mb-3 rounded-lg border border-destructive/25 bg-destructive/5 px-3 py-2">
          <StatusTrail resume={resume} compact />
        </div>
      ) : skills.length > 0 ? (
        <div className="flex flex-wrap gap-1 px-4 pb-3">
          {skills.map(s => (
            <span key={s} className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-foreground">{s}</span>
          ))}
          {more > 0 && <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">+{more}</span>}
        </div>
      ) : null}

      {done && pending && !canAccept && ats != null && (
        <p className="px-4 pb-2 text-[10px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Below pass threshold ({thresholdFor(resume, passThreshold)}%)
        </p>
      )}

      {/* Footer */}
      <div className="mt-auto border-t border-border/60 bg-muted/20 px-4 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {awaiting
            ? <span className="inline-flex items-center rounded-full border border-violet-500/25 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-400">
                Not evaluated
              </span>
            : <StatusBadge status={resume.status} />}
          <DecisionBadge status={resume.candidateStatus} />
        </div>
        <p className="text-[10px] text-muted-foreground shrink-0">
          {new Date(resume.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
        </p>
      </div>

      {/* Action row */}
      <div className="flex items-center gap-1 border-t border-border/60 px-3 py-2 bg-card">
        <Button variant="ghost" size="sm" className="flex-1 h-7 gap-1 text-xs" onClick={() => onView(resume)} disabled={processing || awaiting}>
          <Eye className="h-3 w-3" />View
        </Button>
        {canManage && resume.status !== 'completed' && (
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
            onClick={() => onOpenFile(resume)}
            aria-label="Open the uploaded resume"
            title="Open the uploaded file — removed once evaluated">
            <FileText className="h-3.5 w-3.5" />
          </Button>
        )}
        {canManage && awaiting && !bulkMode && (
          <Button
            variant="ghost"
            size="sm"
            className="flex-[2] h-7 gap-1 text-xs text-violet-600 dark:text-violet-400 hover:text-violet-700"
            onClick={() => onEvaluate(resume)}
            disabled={evaluating}
            title="Parse this resume and score it against the job description"
          >
            {evaluating
              ? <><Loader2 className="h-3 w-3 animate-spin" />Starting…</>
              : <><Sparkles className="h-3 w-3" />Evaluate</>}
          </Button>
        )}
        {canManage && done && pending && !bulkMode && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'flex-1 h-7 gap-1 text-xs',
                canAccept
                  ? 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700'
                  : 'text-muted-foreground opacity-60 cursor-not-allowed'
              )}
              onClick={() => canAccept && onSelect(resume)}
              disabled={selecting || !canAccept}
              title={
                canAccept
                  ? 'Accept candidate and create account'
                  : `ATS ${ats ?? 0}% is below pass threshold (${thresholdFor(resume, passThreshold)}%)`
              }
            >
              <CheckCircle2 className="h-3 w-3" />Select
            </Button>
            <Button variant="ghost" size="sm" className="flex-1 h-7 gap-1 text-xs text-destructive hover:text-destructive"
              onClick={() => onReject(resume)} disabled={selecting}>
              <XCircle className="h-3 w-3" />Reject
            </Button>
          </>
        )}
        {!bulkMode && <ResumeDownloadMenu resume={resume} />}
        {canManage && (
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(resume)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
        )}
      </div>
    </div>
  );
}


/**
 * Self-declared skill match, shown before any AI evaluation has run.
 *
 * Its purpose is triage: a candidate matching few required skills is a safe
 * skip, and every skip is an evaluation not paid for. Unverified, so it is
 * styled as a hint rather than a score.
 */
function SkillMatchBadge({ match, compact = false }) {
  if (!match || !match.totalSkills) return null;

  const { requiredMatched, requiredTotal, totalMatched, totalSkills, percent } = match;
  const tone = percent >= 80 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25'
    : percent >= 50 ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25'
    : 'bg-muted text-muted-foreground border-border';

  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', tone)}
      title={`Declared ${totalMatched} of ${totalSkills} skills${requiredTotal ? `, ${requiredMatched} of ${requiredTotal} required` : ''}. Self-reported — not verified.`}
    >
      <Target className="h-2.5 w-2.5" />
      {compact
        ? `${totalMatched}/${totalSkills}`
        : requiredTotal > 0
          ? `${totalMatched}/${totalSkills} skills · ${requiredMatched}/${requiredTotal} required`
          : `${totalMatched}/${totalSkills} skills`}
    </span>
  );
}

/* ── List row — dense alternative to the card, built for long shortlists ── */

function ResumeListRow({ resume, onView, onOpenFile, onSelect, onReject, onDelete, onEvaluate, evaluatingId,
                         selecting, selected, onToggleSelect, bulkMode, passThreshold, canManage }) {
  const name  = resume.parsedData?.name  || resume.applicantName  || resume.fileName;
  const email = resume.parsedData?.email || resume.applicantEmail || '';
  const ats   = resume.atsScore;
  const done  = resume.status === 'completed';
  const awaiting   = isAwaitingEvaluation(resume);
  const evaluating = evaluatingId === resume._id;
  const processing = !TERMINAL_STATUSES.has(resume.status) && !awaiting;
  const pending    = !resume.candidateStatus || resume.candidateStatus === 'pending';
  const canAccept  = meetsPassThreshold(resume, passThreshold);
  // Selectable to accept, or to evaluate if that has not happened yet.
  const selectable = (done && pending && canAccept) || awaiting || resume.status === 'failed';
  const tone       = done && ats != null ? atsTone(ats) : null;

  return (
    <div className={cn(
      'group grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2.5 transition-colors hover:bg-muted/40',
      selected && 'bg-primary/5',
    )}>
      {/* Checkbox + identity */}
      <div className="col-span-12 flex min-w-0 items-center gap-2.5 sm:col-span-4">
        {bulkMode && canManage && selectable && (
          <button type="button" onClick={() => onToggleSelect(resume._id)} className="shrink-0 text-primary">
            {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-muted-foreground" />}
          </button>
        )}
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-foreground">{name}</p>
          {email && <p className="truncate text-[11px] text-muted-foreground">{email}</p>}
        </div>
      </div>

      {/* Job description */}
      <div className="col-span-6 hidden min-w-0 sm:col-span-3 sm:block">
        <p className="truncate text-[11px] text-muted-foreground">{resume.templateId?.title || '—'}</p>
      </div>

      {/* Score */}
      <div className="col-span-3 sm:col-span-2">
        {done && ats != null ? (
          <div className="flex items-center gap-1.5">
            <span className={cn('text-xs font-bold tabular-nums', tone.fg)}>{ats}%</span>
            <span className="hidden text-[10px] text-muted-foreground lg:inline">{resume.hiringTier || ''}</span>
          </div>
        ) : awaiting ? (
          <span className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-violet-600 dark:text-violet-400">Not evaluated</span>
            <SkillMatchBadge match={resume.skillMatch} compact />
          </span>
        ) : (
          <StatusTrail resume={resume} compact />
        )}
      </div>

      {/* Decision */}
      <div className="col-span-3 hidden sm:col-span-1 sm:block">
        <DecisionBadge status={resume.candidateStatus} />
      </div>

      {/* Actions */}
      <div className="col-span-12 flex items-center justify-end gap-0.5 sm:col-span-2">
        {canManage && awaiting && !bulkMode && (
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[11px] text-violet-600 dark:text-violet-400"
            onClick={() => onEvaluate(resume)} disabled={evaluating}>
            {evaluating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            Evaluate
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onView(resume)}
          disabled={processing || awaiting} aria-label="View details">
          <Eye className="h-3.5 w-3.5" />
        </Button>
        {canManage && resume.status !== 'completed' && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenFile(resume)}
            aria-label="Open the uploaded resume" title="Open the uploaded file — removed once evaluated">
            <FileText className="h-3.5 w-3.5" />
          </Button>
        )}
        {canManage && done && pending && !bulkMode && (
          <>
            <Button variant="ghost" size="icon"
              className={cn('h-7 w-7', canAccept ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground opacity-50')}
              onClick={() => canAccept && onSelect(resume)} disabled={selecting || !canAccept}
              aria-label="Accept candidate"
              title={canAccept ? 'Accept candidate' : `ATS ${ats ?? 0}% is below the ${thresholdFor(resume, passThreshold)}% threshold`}>
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
              onClick={() => onReject(resume)} disabled={selecting} aria-label="Reject candidate">
              <XCircle className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {!bulkMode && <ResumeDownloadMenu resume={resume} />}
        {canManage && (
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
            onClick={() => onDelete(resume)} aria-label="Delete resume">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ── Upload dialog (async — queues immediately, polling handles progress) ── */
function UploadDialog({ open, onClose, templates, onQueued, onAddJD }) {
  const [templateId, setTemplateId] = useState('');
  const [files, setFiles]           = useState([]);
  const [dragging, setDragging]     = useState(false);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState('');
  const [duplicate, setDuplicate]   = useState(null);
  const [queued, setQueued]         = useState(false);  // show "queued" success state
  const inputRef = useRef(null);

  const ALLOWED = ['application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword', 'text/plain'];

  const accept = (incoming) => {
    setError('');
    const slots = MAX_FILES - files.length;
    if (slots <= 0) { setError(`Maximum ${MAX_FILES} resumes per batch. Remove a file to add another.`); return; }
    const candidates = Array.from(incoming);
    const valid = [];
    for (const f of candidates) {
      if (!ALLOWED.includes(f.type)) { setError(`"${f.name}" is not a supported type (PDF, DOCX, DOC, TXT).`); continue; }
      if (f.size > 10 * 1024 * 1024) { setError(`"${f.name}" exceeds 10 MB.`); continue; }
      if (files.some(existing => existing.name === f.name && existing.size === f.size)) continue; // skip exact dup
      valid.push(f);
    }
    const toAdd = valid.slice(0, slots);
    if (valid.length > slots) setError(`Only ${slots} more slot${slots !== 1 ? 's' : ''} available. First ${slots} file${slots !== 1 ? 's' : ''} added.`);
    if (toAdd.length) setFiles(prev => [...prev, ...toAdd]);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files); };

  const submit = async (e) => {
    e.preventDefault();
    if (!templateId || files.length === 0) return;
    setBusy(true); setError(''); setDuplicate(null);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append('resume', files[0]);
        fd.append('templateId', templateId);
        const res = await apiClient.uploadResume(fd);
        if (res.duplicate) setDuplicate(res.duplicate);
        onQueued?.(res.data);
      } else {
        const fd = new FormData();
        files.forEach(f => fd.append('resumes', f));
        fd.append('templateId', templateId);
        const res = await apiClient.bulkUploadResumes(fd);
        res.data?.forEach(r => onQueued?.(r));
      }
      setQueued(true);
      // Auto-close after 2.5s to let user read the "safe to close" message
      setTimeout(() => { reset(); onClose(); }, 2500);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setBusy(false);
    }
  };

  const removeFile = (idx) => { if (!busy) setFiles(prev => prev.filter((_, i) => i !== idx)); };
  const reset = () => {
    setTemplateId(''); setFiles([]); setBusy(false);
    setError(''); setDuplicate(null); setQueued(false);
    if (inputRef.current) inputRef.current.value = '';
  };
  const handleClose = () => { if (!busy) { reset(); onClose(); } };

  const slots = MAX_FILES - files.length;
  const atLimit = files.length >= MAX_FILES;

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: t._id,
        label: t.title,
        sub: [t.position, t.department].filter(Boolean).join(' · ') || undefined,
      })),
    [templates]
  );

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      {/*
        Override DialogContent default grid+padding to get a flex column layout.
        The inner wrapper (.modal-shell) controls the actual flex structure.
        w-[calc(100%-2rem)]: full width minus 1rem gutter on each side on mobile
        sm:max-w-md: fixed width on larger screens
      */}
      <DialogContent className="w-[calc(100%-2rem)] sm:w-full sm:max-w-md p-0 gap-0 max-h-[90vh] overflow-hidden rounded-xl">
        {/* ── Inner flex shell — owns the sticky header / scroll body / sticky footer ── */}
        <div className="flex flex-col max-h-[90vh] overflow-hidden rounded-xl">

          {/* Sticky header */}
          <div className="shrink-0 flex items-center gap-2 px-5 pt-5 pb-4 border-b border-border/60">
            <CloudUpload className="h-5 w-5 text-primary shrink-0" />
            <DialogTitle className="text-base font-semibold leading-snug flex-1 min-w-0">
              Upload Resume{files.length > 1 ? 's' : ''}
            </DialogTitle>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4">

          {/* ── Queued success state ── */}
          {queued ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  {files.length === 1 ? '1 resume queued!' : `${files.length} resumes queued!`}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Processing runs in the background on our servers.
                </p>
              </div>
              <div className="flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px] text-primary">
                <Zap className="h-3 w-3 shrink-0" />
                You can safely close this tab or reload — progress is saved automatically.
              </div>
              <p className="text-[11px] text-muted-foreground">This dialog closes in a moment…</p>
            </div>
          ) : (
            <form id="upload-form" onSubmit={submit} className="space-y-4">
              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive">
                  {error}
                </div>
              )}
              {duplicate && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                  <Copy className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Identical file already processed for <strong>{duplicate.name || duplicate.fileName}</strong> (ATS: {duplicate.atsScore}%). Processing anyway as a new entry.
                  </span>
                </div>
              )}

              <SearchableSelect
                id="upload-tmpl"
                label="Job template"
                value={templateId}
                onChange={setTemplateId}
                options={templateOptions}
                placeholder="Search and select a template…"
                searchPlaceholder="Search templates…"
                disabled={busy}
                required
                emptyMessage="No templates match your search."
              />

              {/* Without a job description there is nothing to score a resume
                  against, so offer the way out rather than a dead dropdown. */}
              {templates.length === 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      You have no job descriptions yet — a resume is scored against one, so you need at least one first.
                    </p>
                    <Button
                      type="button" size="sm" variant="outline"
                      className="mt-2 h-7 gap-1.5 text-[11px]"
                      onClick={onAddJD}
                    >
                      <FileText className="h-3 w-3" /> Create a job description
                    </Button>
                  </div>
                </div>
              )}

              {/* Drop zone */}
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Label className="text-xs shrink-0">
                    Resume files
                    <span className="ml-1 text-muted-foreground font-normal">
                      ({files.length}/{MAX_FILES} added)
                    </span>
                  </Label>
                  {atLimit && (
                    <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 shrink-0">Limit reached</span>
                  )}
                </div>

                <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.txt" multiple className="sr-only" id="resume-input"
                  onChange={e => accept(e.target.files)} disabled={busy || atLimit} />
                <label htmlFor="resume-input"
                  onDragOver={e => { e.preventDefault(); if (!atLimit) setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  className={cn(
                    'flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 transition-colors',
                    atLimit      ? 'cursor-not-allowed opacity-50 border-border'
                    : dragging    ? 'border-primary bg-primary/5'
                    : files.length > 0 ? 'border-emerald-500/50 bg-emerald-500/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/30'
                  )}>
                  <CloudUpload className="h-7 w-7 text-muted-foreground" />
                  {atLimit
                    ? <p className="text-xs text-muted-foreground">Max {MAX_FILES} files reached</p>
                    : <>
                      <p className="text-xs text-muted-foreground text-center">Drag & drop or click to browse</p>
                      <p className="text-[11px] text-muted-foreground text-center">PDF, DOCX, DOC — max 10 MB each · up to {MAX_FILES} per batch</p>
                    </>
                  }
                </label>

                {/* File list */}
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-1.5">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
                        <span className="flex-1 truncate text-[11px] font-medium text-foreground min-w-0">{f.name}</span>
                        <span className="shrink-0 text-[10px] text-muted-foreground whitespace-nowrap">
                          {formatFileSize(f.size)}
                        </span>
                        {!busy && (
                          <button type="button" onClick={() => removeFile(i)}
                            className="shrink-0 text-muted-foreground hover:text-destructive transition-colors">
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {slots > 0 && (
                      <p className="text-[10px] text-muted-foreground pl-1">{slots} more slot{slots !== 1 ? 's' : ''} available</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px] text-primary">
                <Zap className="h-3 w-3 shrink-0 mt-0.5" />
                <span>Queued instantly — AI processing runs on our servers. Safe to close your tab or reload.</span>
              </div>
            </form>
          )}
        </div>{/* end scrollable body */}

        {/* Sticky footer */}
        {!queued && (
          <div className="shrink-0 flex items-center justify-end gap-2 border-t border-border/60 bg-background px-5 py-3">
            <Button variant="outline" size="sm" onClick={handleClose} disabled={busy}>Cancel</Button>
            <Button type="submit" form="upload-form" size="sm"
              disabled={busy || !templateId || files.length === 0} className="gap-1.5">
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Queuing…</>
                : <><Upload className="h-3.5 w-3.5" />{files.length > 1 ? `Queue ${files.length} resumes` : 'Queue resume'}</>}
            </Button>
          </div>
        )}
        </div>{/* end flex shell */}
      </DialogContent>
    </Dialog>
  );
}

/* ── Background sync banner ─────────────────────────────────────── */
function BackgroundSyncBanner({ processingResumes }) {
  const count = processingResumes.length;
  if (count === 0) return null;

  // Aggregate step distribution for a richer label
  const stepCounts = processingResumes.reduce((acc, r) => {
    const step = r.processingStep || r.status || 'queued';
    acc[step] = (acc[step] || 0) + 1;
    return acc;
  }, {});

  const stepLabel = (() => {
    if (stepCounts.scoring)    return `scoring ${stepCounts.scoring}`;
    if (stepCounts.parsing)    return `parsing ${stepCounts.parsing}`;
    if (stepCounts.extracting) return `extracting ${stepCounts.extracting}`;
    return `${count} queued`;
  })();

  return (
    <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-foreground">
          {count} resume{count !== 1 ? 's' : ''} processing — {stepLabel}…
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Running on the server. Safe to close or reload this tab — progress is saved automatically.
        </p>
      </div>
      <div className="shrink-0 hidden sm:block">
        <div className="flex gap-0.5">
          {processingResumes.slice(0, MAX_FILES).map((r, i) => {
            const step = r.processingStep || r.status;
            const pct  = step === 'scoring' ? 85 : step === 'parsing' ? 55 : step === 'extracting' ? 25 : 5;
            return (
              <div key={r._id || i} className="w-1 rounded-full bg-muted overflow-hidden" style={{ height: 28 }}>
                <div className="w-full rounded-full bg-primary transition-all duration-700"
                  style={{ height: `${pct}%`, marginTop: `${100 - pct}%` }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Full-screen resume detail view ─────────────────────────────── */
const TABS = [
  { id: 'overview',   label: 'Overview',   icon: Info },
  { id: 'experience', label: 'Experience', icon: Building2 },
  { id: 'education',  label: 'Education',  icon: GraduationCap },
  { id: 'ai',         label: 'AI Analysis',icon: Sparkles },
];

function ScorePill({ label, value }) {
  const { fg, bar } = atsTone(value || 0);
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-card p-3">
      <div className="relative h-14 w-14">
        <svg viewBox="0 0 56 56" className="-rotate-90 h-14 w-14 absolute inset-0">
          <circle cx="28" cy="28" r="22" fill="none" strokeWidth="5" stroke="currentColor" className="text-muted/30" />
          <circle cx="28" cy="28" r="22" fill="none" strokeWidth="5"
            stroke={atsTone(value || 0).ring}
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 22}
            strokeDashoffset={2 * Math.PI * 22 - ((value || 0) / 100) * 2 * Math.PI * 22} />
        </svg>
        <span className={cn('absolute inset-0 flex items-center justify-center text-[11px] font-black tabular-nums', fg)}>
          {value || 0}%
        </span>
      </div>
      <span className="text-center text-[10px] font-semibold text-muted-foreground leading-tight">{label}</span>
    </div>
  );
}

function ResumeDetailView({ resume, onClose, onSelect, onReject, selecting, passThreshold, canManage }) {
  const [activeTab, setActiveTab] = useState('overview');
  const contentRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!resume) return null;
  const p    = resume.parsedData || {};
  const an   = resume.resumeAnalysis || {};
  const bd   = resume.scoreBreakdown || {};
  const ats  = resume.atsScore || 0;
  const done = resume.status === 'completed';
  const pending = !resume.candidateStatus || resume.candidateStatus === 'pending';
  const canAccept = meetsPassThreshold(resume, passThreshold);
  const { fg } = atsTone(ats);

  const name     = p.name || resume.fileName || 'Unknown';
  const initials = name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

  const handleTabChange = (id) => {
    setActiveTab(id);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* ── Top header bar ── */}
      <div className="shrink-0 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Back */}
          <button onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </button>

          {/* Avatar + name */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-sm font-black text-primary">
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-bold text-foreground leading-tight">{name}</h2>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0">
                {p.email    && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Mail className="h-2.5 w-2.5 shrink-0" />{p.email}</span>}
                {p.phone    && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Phone className="h-2.5 w-2.5 shrink-0" />{p.phone}</span>}
                {p.location && <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-2.5 w-2.5 shrink-0" />{p.location}</span>}
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="hidden items-center gap-1.5 sm:flex shrink-0">
            <StatusBadge status={resume.status} />
            <DecisionBadge status={resume.candidateStatus} />
            {resume.templateId?.title && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
                <Briefcase className="h-2.5 w-2.5 shrink-0" />{resume.templateId.title}
              </span>
            )}
          </div>

          {/* Download — needs only view access, and stays available after the
              candidate is selected or rejected, unlike Select/Reject below. */}
          {done && <ResumeDownloadButtons resume={resume} className="flex shrink-0 gap-1.5" />}

          {/* Actions */}
          {canManage && done && pending && (
            <div className="flex shrink-0 items-center gap-1.5">
              <Button size="sm" variant="outline"
                className={cn(
                  'h-7 gap-1 text-xs border-emerald-500/30',
                  canAccept
                    ? 'text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-700 dark:text-emerald-400'
                    : 'text-muted-foreground opacity-60 cursor-not-allowed'
                )}
                onClick={() => canAccept && onSelect(resume)}
                disabled={selecting || !canAccept}
                title={
                  canAccept
                    ? 'Accept candidate and create account'
                    : `ATS ${ats}% is below pass threshold (${thresholdFor(resume, passThreshold)}%)`
                }>
                <CheckCircle2 className="h-3 w-3" /><span className="hidden sm:inline">Select</span>
              </Button>
              <Button size="sm" variant="outline"
                className="h-7 gap-1 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => onReject(resume)} disabled={selecting}>
                <XCircle className="h-3 w-3" /><span className="hidden sm:inline">Reject</span>
              </Button>
            </div>
          )}
        </div>

        {/* ── Tab navigation ── */}
        <div className="flex gap-0 border-t border-border/50 px-4">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => handleTabChange(id)}
              className={cn(
                'flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition-colors',
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-4 py-6 lg:grid lg:grid-cols-[280px_1fr] lg:gap-6">

          {/* ── Left sidebar (ATS + breakdown + skills) ── */}
          <aside className="mb-6 lg:mb-0 space-y-4">
            {/* ATS score card */}
            {done ? (
              <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className={cn(
                  'flex flex-col items-center gap-2 px-5 py-5',
                  ats >= 80 ? 'bg-emerald-500/8' : ats >= 60 ? 'bg-amber-500/8' : 'bg-red-500/8'
                )}>
                  <AtsRing score={ats} size={96} />
                  <div className="text-center">
                    <p className={cn('text-lg font-black tabular-nums', fg)}>{ats}%</p>
                    <p className="text-[11px] text-muted-foreground font-medium">ATS Match Score</p>
                  </div>
                  {/* Hiring tier badge */}
                  {(() => {
                    const tier = resume.hiringTier ||
                      (ats >= 85 ? 'Strong Match' : ats >= 70 ? 'Good Match' : ats >= 50 ? 'Partial Match' : 'Weak Match');
                    const tierCls = tier === 'Strong Match'  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                                  : tier === 'Good Match'    ? 'bg-teal-500/15 text-teal-700 dark:text-teal-400'
                                  : tier === 'Partial Match' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                                  : 'bg-red-500/15 text-red-700 dark:text-red-400';
                    return (
                      <div className={cn('rounded-full px-3 py-0.5 text-[11px] font-semibold', tierCls)}>
                        {tier}
                      </div>
                    );
                  })()}
                </div>

                {/* 6-dimension breakdown bars with reasoning */}
                {Object.keys(bd).length > 0 && (
                  <div className="space-y-3 px-4 py-4 border-t border-border/50">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Score Breakdown</p>
                    {[
                      { key: 'technicalSkills',     label: 'Technical Skills',     weight: '35%' },
                      { key: 'experienceRelevance', label: 'Experience Relevance', weight: '25%' },
                      { key: 'keywordCoverage',     label: 'Keyword Coverage',     weight: '15%' },
                      { key: 'educationFit',        label: 'Education Fit',        weight: '10%' },
                      { key: 'achievementQuality',  label: 'Achievement Quality',  weight: '10%' },
                      { key: 'seniorityAlignment',  label: 'Seniority Alignment',  weight: '5%'  },
                      // Legacy fallback fields
                      { key: 'keywordMatch',        label: 'Keyword Match',        weight: '' },
                      { key: 'skillsMatch',         label: 'Skills Match',         weight: '' },
                      { key: 'experienceMatch',     label: 'Experience Match',     weight: '' },
                      { key: 'educationMatch',      label: 'Education Match',      weight: '' },
                    ].filter(({ key }) => bd[key] != null).map(({ key, label, weight }) => {
                      const reasoning = resume.scoreReasoning?.[key];
                      return (
                        <div key={key} className="group relative">
                          <MiniBar label={`${label}${weight ? ` (${weight})` : ''}`} value={bd[key]} />
                          {reasoning && (
                            <p className="mt-0.5 hidden group-hover:block text-[10px] text-muted-foreground leading-snug bg-muted/60 rounded px-2 py-1">
                              {reasoning}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-muted/20 p-5 flex flex-col items-center gap-2">
                {resume.status === 'failed'
                  ? <XCircle className="h-10 w-10 text-destructive" />
                  : <Loader2 className="h-10 w-10 animate-spin text-primary" />}
                <p className="text-xs text-muted-foreground text-center">
                  {resume.status === 'failed' ? 'Processing failed' : 'Processing resume…'}
                </p>
              </div>
            )}

            {/* Quick stats */}
            {(() => {
              const flatSkills = p.skillsFlat || (Array.isArray(p.skills) ? p.skills : Object.values(p.skills || {}).flat());
              return (
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-border bg-card p-3 text-center">
                    <p className="text-base font-black text-foreground">{flatSkills.length || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Skills</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-3 text-center">
                    <p className="text-base font-black text-foreground">
                      {p.totalExperienceYears != null ? `${p.totalExperienceYears}y` : p.experience?.length || 0}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {p.totalExperienceYears != null ? 'Exp' : 'Jobs'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-3 text-center">
                    <p className="text-base font-black text-foreground">{p.education?.length || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Degrees</p>
                  </div>
                </div>
              );
            })()}

            {/* Matched / Missing skills */}
            {(resume.matchedSkills?.length > 0 || resume.missingSkills?.length > 0) && (
              <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <Target className="h-3 w-3 text-primary" />JD Skills Match
                </p>
                {resume.matchedSkills?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">✓ Matched</p>
                    <div className="flex flex-wrap gap-1">
                      {resume.matchedSkills.slice(0, 10).map((s, i) => (
                        <span key={i} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {resume.missingSkills?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-red-600 dark:text-red-400">✗ Missing</p>
                    <div className="flex flex-wrap gap-1">
                      {resume.missingSkills.slice(0, 10).map((s, i) => (
                        <span key={i} className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-400">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Skills (sidebar) — structured */}
            {(() => {
              const isStructured = p.skills && !Array.isArray(p.skills);
              const flatSkills   = p.skillsFlat || (Array.isArray(p.skills) ? p.skills : Object.values(p.skills || {}).flat());
              if (!flatSkills.length) return null;
              const cats = isStructured ? Object.entries(p.skills).filter(([, v]) => v?.length) : null;
              return (
                <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <Target className="h-3 w-3 text-primary" />Skills
                  </p>
                  {cats ? cats.map(([cat, vals]) => (
                    <div key={cat} className="space-y-1">
                      <p className="text-[10px] font-semibold text-muted-foreground capitalize">{cat}</p>
                      <div className="flex flex-wrap gap-1">
                        {vals.map((s, i) => (
                          <span key={i} className="rounded-full border border-primary/20 bg-primary/8 px-2 py-0.5 text-[10px] font-medium text-primary">{s}</span>
                        ))}
                      </div>
                    </div>
                  )) : (
                    <div className="flex flex-wrap gap-1.5">
                      {flatSkills.slice(0, 16).map((s, i) => (
                        <span key={i} className="rounded-full border border-primary/20 bg-primary/8 px-2.5 py-0.5 text-[11px] font-medium text-primary">{s}</span>
                      ))}
                      {flatSkills.length > 16 && (
                        <span className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11px] text-muted-foreground">+{flatSkills.length - 16} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </aside>

          {/* ── Right main content ── */}
          <main className="space-y-4">

            {/* ── OVERVIEW tab ── */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Summary */}
                {p.summary && (
                  <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2.5">
                      <Info className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Professional Summary</span>
                    </div>
                    <p className="px-4 py-4 text-sm leading-relaxed text-foreground">{p.summary}</p>
                  </div>
                )}

                {/* ── Red Flags ── */}
                {resume.redFlags?.length > 0 && (
                  <div className="rounded-2xl border border-amber-500/40 bg-amber-500/6 overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        HR Alert — {resume.redFlags.length} Red Flag{resume.redFlags.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="px-4 py-3 space-y-1.5">
                      {resume.redFlags.map((flag, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />{flag}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── Must-Haves met / missing ── */}
                {(resume.mustHavesMet?.length > 0 || resume.mustHavesMissing?.length > 0) && (
                  <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2.5">
                      <Zap className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Must-Have Requirements</span>
                    </div>
                    <div className="px-4 py-4 space-y-3">
                      {resume.mustHavesMet?.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">✓ Met</p>
                          <div className="flex flex-wrap gap-1.5">
                            {resume.mustHavesMet.map((s, i) => (
                              <span key={i} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {resume.mustHavesMissing?.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wide">✗ Missing</p>
                          <div className="flex flex-wrap gap-1.5">
                            {resume.mustHavesMissing.map((s, i) => (
                              <span key={i} className="rounded-full border border-red-500/30 bg-red-500/10 px-3 py-0.5 text-[11px] font-medium text-red-700 dark:text-red-400">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── LinkedIn / GitHub links ── */}
                {(p.linkedIn || p.github) && (
                  <div className="flex flex-wrap gap-2">
                    {p.linkedIn && (
                      <a href={p.linkedIn} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                        <Link2 className="h-3 w-3" />LinkedIn Profile
                      </a>
                    )}
                    {p.github && (
                      <a href={p.github} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                        <Github className="h-3 w-3" />GitHub Profile
                      </a>
                    )}
                  </div>
                )}

                {/* ── All Skills (structured) ── */}
                {(() => {
                  const isStructured = p.skills && !Array.isArray(p.skills);
                  const flatSkills   = p.skillsFlat || (Array.isArray(p.skills) ? p.skills : Object.values(p.skills || {}).flat());
                  if (!flatSkills.length) return null;
                  const cats = isStructured ? Object.entries(p.skills).filter(([, v]) => v?.length) : null;
                  return (
                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2.5">
                        <Target className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">All Skills ({flatSkills.length})</span>
                      </div>
                      <div className="px-4 py-4 space-y-3">
                        {cats ? cats.map(([cat, vals]) => (
                          <div key={cat} className="space-y-1.5">
                            <p className="text-[10px] font-semibold text-muted-foreground capitalize">{cat}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {vals.map((s, i) => (
                                <span key={i} className="rounded-full border border-primary/20 bg-primary/8 px-3 py-0.5 text-xs font-medium text-primary">{s}</span>
                              ))}
                            </div>
                          </div>
                        )) : (
                          <div className="flex flex-wrap gap-1.5">
                            {flatSkills.map((s, i) => (
                              <span key={i} className="rounded-full border border-primary/20 bg-primary/8 px-3 py-1 text-xs font-medium text-primary">{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Candidate description */}
                {resume.candidateDescription && (
                  <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">AI Candidate Description</span>
                    </div>
                    <p className="px-4 py-4 text-sm leading-relaxed text-foreground">{resume.candidateDescription}</p>
                  </div>
                )}

                {/* Error card */}
                {resume.status === 'failed' && (() => {
                  const cat      = resume.errorCategory || 'unknown';
                  const Icon     = cat === 'format'   ? FileText
                                 : cat === 'template' ? Briefcase
                                 : cat === 'size'     ? CloudUpload
                                 : cat === 'network'  ? Zap
                                 : AlertTriangle;
                  const title    = cat === 'format'   ? 'Unreadable file format'
                                 : cat === 'template' ? 'Job template issue'
                                 : cat === 'size'     ? 'File too large'
                                 : cat === 'network'  ? 'Network error'
                                 :                      'Processing failed';
                  const action   = cat === 'format'   ? 'Upload a text-based PDF, DOCX, DOC, or TXT file'
                                 : cat === 'template' ? 'Fix the template\'s job description and re-upload'
                                 : cat === 'size'     ? 'Compress the file and re-upload'
                                 : cat === 'network'  ? 'Re-upload the resume'
                                 :                      'Re-upload the resume';
                  return (
                    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-destructive/20 bg-destructive/8 px-4 py-2.5">
                        <Icon className="h-3.5 w-3.5 text-destructive" />
                        <span className="text-xs font-bold uppercase tracking-wide text-destructive/80">Processing Error — {title}</span>
                      </div>
                      <div className="p-4 space-y-2">
                        {resume.errorMessage && (
                          <p className="text-sm leading-relaxed text-foreground">{resume.errorMessage}</p>
                        )}
                        <div className="flex items-start gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 mt-3">
                          <Info className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" />
                          <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Suggested action:</span> {action}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Mobile badges row */}
                <div className="flex flex-wrap items-center gap-2 sm:hidden">
                  <StatusBadge status={resume.status} />
                  <DecisionBadge status={resume.candidateStatus} />
                  {resume.templateId?.title && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
                      <Briefcase className="h-2.5 w-2.5" />{resume.templateId.title}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── EXPERIENCE tab ── */}
            {activeTab === 'experience' && (
              <div className="space-y-3">
                {p.experience?.length > 0 ? p.experience.map((exp, i) => (
                  <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="flex items-start gap-4 p-5">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <Building2 className="h-4.5 w-4.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-bold text-foreground">{exp.title || exp.position || 'Role'}</p>
                            <p className="text-xs text-muted-foreground font-medium mt-0.5">{exp.company || 'Company'}</p>
                          </div>
                          {exp.duration && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11px] text-muted-foreground shrink-0">
                              <Clock className="h-2.5 w-2.5" />{exp.duration}
                            </span>
                          )}
                        </div>
                        {exp.description && (
                          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{exp.description}</p>
                        )}
                      </div>
                    </div>
                    {i < p.experience.length - 1 && (
                      <div className="mx-5 h-px bg-border/40" />
                    )}
                  </div>
                )) : (
                  <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border">
                    <Building2 className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">No experience data found</p>
                  </div>
                )}
              </div>
            )}

            {/* ── EDUCATION tab ── */}
            {activeTab === 'education' && (
              <div className="space-y-3">
                {p.education?.length > 0 ? p.education.map((edu, i) => (
                  <div key={i} className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <GraduationCap className="h-4.5 w-4.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-bold text-foreground">{edu.degree || edu.qualification || 'Degree'}</p>
                          <p className="text-xs text-muted-foreground font-medium mt-0.5">{edu.institution || edu.school || 'Institution'}</p>
                        </div>
                        {edu.year && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11px] text-muted-foreground shrink-0">
                            <Calendar className="h-2.5 w-2.5" />{edu.year}
                          </span>
                        )}
                      </div>
                      {edu.grade && <p className="mt-1 text-xs text-muted-foreground">Grade: {edu.grade}</p>}
                    </div>
                  </div>
                )) : (
                  <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border">
                    <GraduationCap className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">No education data found</p>
                  </div>
                )}
              </div>
            )}

            {/* ── AI ANALYSIS tab ── */}
            {activeTab === 'ai' && (
              <div className="space-y-4">
                {/* Overall assessment */}
                {an.overallAssessment && (
                  <div className="rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border/60 bg-primary/5 px-4 py-2.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold uppercase tracking-wide text-primary">Overall Assessment</span>
                    </div>
                    <p className="px-4 py-4 text-sm leading-relaxed text-foreground">{an.overallAssessment}</p>
                  </div>
                )}

                {/* Strengths + Weaknesses grid */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {an.strengths?.length > 0 && (
                    <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-2.5">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Strengths</span>
                      </div>
                      <ul className="space-y-2 px-4 py-4">
                        {an.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {an.weaknesses?.length > 0 && (
                    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">Areas to Improve</span>
                      </div>
                      <ul className="space-y-2 px-4 py-4">
                        {an.weaknesses.map((w, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Recommendations */}
                {an.recommendations && (
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/10 px-4 py-2.5">
                      <Star className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold uppercase tracking-wide text-primary">Recommendations</span>
                    </div>
                    <p className="px-4 py-4 text-sm leading-relaxed text-foreground">{an.recommendations}</p>
                  </div>
                )}

                {/* Score mini-cards */}
                {Object.keys(bd).length > 0 && (
                  <div>
                    <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Detailed Score Breakdown</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <ScorePill label="Technical Skills"     value={bd.technicalSkills} />
                      <ScorePill label="Experience"           value={bd.experienceRelevance} />
                      <ScorePill label="Keywords"             value={bd.keywordCoverage} />
                      <ScorePill label="Education"            value={bd.educationFit} />
                      <ScorePill label="Achievements"         value={bd.achievementQuality} />
                      <ScorePill label="Seniority Alignment"  value={bd.seniorityAlignment} />
                    </div>
                  </div>
                )}

                {(!an.overallAssessment && !an.strengths?.length && !an.weaknesses?.length && !an.recommendations) && (
                  <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border">
                    <Sparkles className="h-8 w-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground">{done ? 'No AI analysis available' : 'Analysis not yet complete'}</p>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────── */
export default function ResumePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'manage_resumes');

  const [resumes,   setResumes]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  // Dialogs
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewResume, setViewResume] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectTarget, setSelectTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [missingDetails, setMissingDetails] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [assignConflict, setAssignConflict] = useState(null);
  const [assigning, setAssigning] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [passThreshold, setPassThreshold] = useState(70);

  // Filters
  const [search,         setSearch]         = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterDecision, setFilterDecision] = useState('');
  const [page, setPage] = useState(1);

  // Origin tab — HR uploads vs public job applications
  const [sourceTab,    setSourceTab]    = useState('admin_upload');
  const [evaluatingId, setEvaluatingId] = useState(null);

  // Filters. templateId is applied server-side: a single posting can draw 200+
  // applications, so narrowing by job description keeps the payload small
  // instead of shipping every resume in the company to the browser.
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterScore,    setFilterScore]    = useState('');
  const [filterSkill,    setFilterSkill]    = useState('');
  const [sortBy,         setSortBy]         = useState('recent');
  const [viewMode,       setViewMode]       = useViewMode(VIEW_STORAGE_KEY);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [rRes, tRes, settingsRes] = await Promise.all([
        apiClient.getResumes({ source: sourceTab, templateId: filterTemplate || undefined }),
        apiClient.getTemplates(),
        apiClient.getInterviewSettings().catch(() => ({ data: { passThreshold: 70 } })),
      ]);
      setResumes(rRes.data || []);
      setTemplates(tRes.data || []);
      setPassThreshold(settingsRes.data?.passThreshold ?? 70);
    } catch { setError('Failed to load data.'); }
    finally { if (!silent) setLoading(false); }
  }, [sourceTab, filterTemplate]);

  // Resumes with work actually in flight. Applications parked in `queued` are
  // waiting on a human, so they are deliberately excluded — polling them would
  // never resolve and would keep the sync banner up forever.
  const processingResumes = useMemo(
    () => resumes.filter(r => !TERMINAL_STATUSES.has(r.status) && !isAwaitingEvaluation(r)),
    [resumes],
  );

  // When the last in-flight resume finishes, flash a completion message
  const prevProcessingCountRef = useRef(null);
  useEffect(() => {
    const count = processingResumes.length;
    if (prevProcessingCountRef.current !== null &&
        prevProcessingCountRef.current > 0 &&
        count === 0) {
      setSuccess('All queued resumes have finished processing.');
    }
    prevProcessingCountRef.current = count;
  }, [processingResumes.length]);

  // ── Polling: update any in-progress resumes every 2.5s ────────────
  useEffect(() => {
    const processingResumes = resumes.filter(
      r => !TERMINAL_STATUSES.has(r.status) && !isAwaitingEvaluation(r)
    );
    if (processingResumes.length === 0) return;

    const iv = setInterval(async () => {
      const updates = await Promise.allSettled(
        processingResumes.map(r => apiClient.getResumeStatus(r._id))
      );

      setResumes(prev => {
        let changed = false;
        const next = prev.map(r => {
          const hit = updates.find((u, i) => u.status === 'fulfilled' && processingResumes[i]._id === r._id);
          if (!hit) return r;
          const status = hit.value;
          if (status.status === r.status && status.processingStep === r.processingStep) return r;
          changed = true;
          return {
            ...r,
            status:          status.status,
            processingStep:  status.processingStep,
            atsScore:        status.atsScore       ?? r.atsScore,
            hiringTier:      status.hiringTier     ?? r.hiringTier,
            candidateStatus: status.candidateStatus ?? r.candidateStatus,
            // Merge error details immediately so the ResumeCard error snippet
            // appears as soon as the poll detects a failure (no manual refresh needed).
            errorMessage:    status.errorMessage   ?? r.errorMessage,
            errorCategory:   status.errorCategory  ?? r.errorCategory,
            parsedData:      r.parsedData ?? {
              name:  status.candidateName,
              email: status.candidateEmail,
            },
          };
        });
        return changed ? next : prev;
      });

      // If any just completed, do a full refresh to get parsedData + scores
      const justCompleted = updates.some(
        (u, i) => u.status === 'fulfilled' &&
          u.value.status === 'completed' &&
          processingResumes[i].status !== 'completed'
      );
      // Refresh in place — a skeleton flash here would be jarring mid-review.
      if (justCompleted) load({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(iv);
  }, [resumes, load]);

  // ── When a new resume is queued from the dialog ────────────────────
  const handleQueued = useCallback((queuedResume) => {
    setResumes(prev => [queuedResume, ...prev]);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Trigger AI evaluation for a public application ─────────────────
  const handleEvaluate = useCallback(async (resume) => {
    setEvaluatingId(resume._id);
    setError('');
    try {
      await apiClient.parseResume(resume._id);
      // Flip to a processing state locally so the pipeline UI and the existing
      // status poller take over immediately, without waiting for a refetch.
      setResumes(prev => prev.map(r => r._id === resume._id
        ? { ...r, status: 'extracting', processingStep: 'extracting' }
        : r
      ));
    } catch (e) {
      setError(friendlyApiError(e, 'Could not start the evaluation. Please try again.'));
    } finally {
      setEvaluatingId(null);
    }
  }, []);

  // KPIs — labels differ per tab, so expose both framings from one pass
  const kpis = useMemo(() => ({
    total:     resumes.length,
    completed: resumes.filter(r => r.status === 'completed').length,
    selected:  resumes.filter(r => r.candidateStatus === 'selected').length,
    rejected:  resumes.filter(r => r.candidateStatus === 'rejected').length,
    awaiting:  resumes.filter(isAwaitingEvaluation).length,
  }), [resumes]);

  // Filter, then sort. Everything except the job description runs client-side so
  // typing in the search box never triggers a refetch.
  const filtered = useMemo(() => {
    const q    = search.trim().toLowerCase();
    const band = SCORE_BANDS[filterScore];

    const out = resumes.filter(r => {
      if (filterStatus   && r.status !== filterStatus) return false;
      if (filterDecision && (r.candidateStatus || 'pending') !== filterDecision) return false;
      if (band) {
        // Only scored resumes can sit in a band — unevaluated ones have no score.
        if (r.status !== 'completed' || r.atsScore == null) return false;
        if (r.atsScore < band.min || r.atsScore > band.max) return false;
      }
      if (filterSkill && !SKILL_BANDS[filterSkill]?.test(r.skillMatch)) return false;
      if (!q) return true;
      // Unevaluated applications are only searchable by their form details.
      const name  = (r.parsedData?.name  || r.applicantName  || r.fileName || '').toLowerCase();
      const email = (r.parsedData?.email || r.applicantEmail || '').toLowerCase();
      const tmpl  = (r.templateId?.title || '').toLowerCase();
      return name.includes(q) || email.includes(q) || tmpl.includes(q);
    });

    return out.sort(SORTS[sortBy]?.fn || SORTS.recent.fn);
  }, [resumes, search, filterStatus, filterDecision, filterScore, filterSkill, sortBy]);

  // Rows are far shorter than cards, so the list view can show many more per page
  // without scrolling forever — the point of that view on a 200-applicant posting.
  const pageSize   = viewMode === 'list' ? LIST_PAGE_SIZE : PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  // Changing page size can strand the viewer past the last page.
  useEffect(() => { setPage(p => Math.min(p, totalPages)); }, [totalPages]);

  const changeFilter = (fn) => { fn(); setPage(1); };

  // Actions
  const handleView = async (r) => {
    try { const res = await apiClient.getResumeById(r._id); setViewResume(res.data); }
    catch { setError('Failed to load details.'); }
  };

  const handleOpenFile = async (r) => {
    try {
      await apiClient.openResumeFile(r._id);
    } catch (e) {
      setError(friendlyApiError(e, 'Could not open the uploaded resume.'));
    }
  };

  /** Queue every eligible selection at once; the server paces the work. */
  const handleBulkEvaluate = async () => {
    const ids = [...selectedIds];
    if (!ids.length) { setError('Select at least one applicant first.'); return; }
    setBulkLoading(true);
    setError('');
    try {
      const res = await apiClient.bulkParseResumes(ids);
      setSuccess(res.message);
      // Flip them locally so the existing poller picks progress up immediately.
      setResumes(prev => prev.map(r =>
        ids.includes(r._id) && r.status !== 'completed'
          ? { ...r, status: 'queued', processingStep: 'queued' }
          : r
      ));
      clearBulkSelection();
    } catch (e) {
      setError(friendlyApiError(e, 'Could not queue those evaluations.'));
    } finally {
      setBulkLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await apiClient.deleteResume(deleteTarget._id); setDeleteTarget(null); load(); }
    catch (e) { setError(e.message || 'Delete failed.'); setDeleteTarget(null); }
  };

  const confirmSelect = async (maybeDetails) => {
    if (!selectTarget) return;
    // Callers wired straight to a click handler pass the React event; only a
    // plain object is real detail, and anything else must not reach the body —
    // JSON.stringify on an event throws on its circular fiber reference.
    const details = isPlainObject(maybeDetails) ? maybeDetails : undefined;
    if (!meetsPassThreshold(selectTarget, passThreshold)) {
      setError(`This resume scored ${selectTarget.atsScore ?? 0}%, which is below the ${thresholdFor(selectTarget, passThreshold)}% pass threshold for this job description.`);
      setSelectTarget(null);
      return;
    }
    setSelecting(true);
    try {
      const res = await apiClient.selectCandidate(selectTarget._id, details);
      const pwd = res.data?.defaultPassword;
      const name = details?.name || selectTarget.parsedData?.name || 'candidate';
      const email = details?.email || selectTarget.parsedData?.email;
      setSuccess(res.message || `Account created for ${name}. They can log in with ${email} / ${pwd}.`);
      setSelectTarget(null);
      setMissingDetails(null);
      load();
    } catch (e) {
      if (e.status === 409 && e.code === 'EMAIL_ALREADY_EXISTS' && e.data?.existingUser) {
        setSelectTarget(null);
        setAssignConflict({ resumeId: selectTarget._id, existingUser: e.data.existingUser });
      } else if (e.code === 'MISSING_CANDIDATE_DETAILS' && e.data?.missingFields?.length) {
        // Parsing could not read a name or email — ask for it instead of
        // dead-ending on "upload a clearer file".
        setMissingDetails({ resume: selectTarget, fields: e.data.missingFields });
        setSelectTarget(null);
      } else {
        setError(friendlyApiError(e, 'Could not create the candidate account. Please try again.'));
        setSelectTarget(null);
      }
    } finally {
      setSelecting(false);
    }
  };

  /** Retry acceptance with the details HR just typed. */
  const submitMissingDetails = async (values) => {
    const target = missingDetails?.resume;
    if (!target) return;
    setSelecting(true);
    try {
      const res = await apiClient.selectCandidate(target._id, values);
      const pwd = res.data?.defaultPassword;
      setSuccess(res.message || `Account created for ${values.name || 'the candidate'}. Password: ${pwd}.`);
      setMissingDetails(null);
      load();
    } catch (e) {
      if (e.status === 409 && e.code === 'EMAIL_ALREADY_EXISTS' && e.data?.existingUser) {
        setMissingDetails(null);
        setAssignConflict({ resumeId: target._id, existingUser: e.data.existingUser });
      } else {
        setError(friendlyApiError(e, 'Could not create the candidate account. Please try again.'));
      }
    } finally {
      setSelecting(false);
    }
  };

  // Bulk helpers
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkSelectAll = () => {
    // On the applicants tab the useful batch is everything still unevaluated —
    // that is what "Evaluate selected" acts on. Elsewhere it stays the set that
    // can be accepted.
    if (isApplicantsTab) {
      const unevaluated = filtered.filter(r => isAwaitingEvaluation(r) || r.status === 'failed');
      setSelectedIds(new Set(unevaluated.map(r => r._id)));
      if (!unevaluated.length) setError('Every applicant in this view has already been evaluated.');
      return;
    }
    const eligible = filtered.filter(
      r => r.status === 'completed' && r.candidateStatus === 'pending' && meetsPassThreshold(r, passThreshold)
    );
    setSelectedIds(new Set(eligible.map(r => r._id)));
    if (eligible.length === 0) {
      setError('No pending resumes scored at or above the pass threshold for their job description.');
    }
  };

  const clearBulkSelection = () => { setSelectedIds(new Set()); setBulkMode(false); };

  const handleBulkDecision = async (decision) => {
    if (selectedIds.size === 0) {
      setError('Please select at least one resume first.');
      return;
    }
    setBulkLoading(true);
    setError('');
    try {
      const res = await apiClient.bulkDecisionResumes([...selectedIds], decision);
      setSuccess(res.message || `${res.modified} candidate${res.modified === 1 ? '' : 's'} ${decision === 'selected' ? 'accepted' : 'rejected'}.`);
      clearBulkSelection();
      load();
    } catch (e) {
      const skipped = e.data?.skipped || [];
      let msg = friendlyApiError(
        e,
        decision === 'selected'
          ? 'No candidate accounts could be created. Please review the selected resumes and try again.'
          : 'Could not reject the selected candidates. Please try again.'
      );
      const detail = formatSkippedCandidates(skipped);
      if (detail && !msg.includes(detail)) msg = `${msg} ${detail}`;
      setError(msg);
    } finally { setBulkLoading(false); }
  };

  const confirmAssign = async () => {
    if (!assignConflict) return;
    setAssigning(true);
    try {
      const res = await apiClient.assignResumeToExistingUser(assignConflict.resumeId, assignConflict.existingUser._id);
      setAssignConflict(null);
      setSuccess(res.message || `Resume linked to ${assignConflict.existingUser.firstName} ${assignConflict.existingUser.lastName}.`);
      load();
    } catch (e) {
      setError(friendlyApiError(e, 'Could not link this resume to the existing candidate. Please try again.'));
    } finally {
      setAssigning(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setSelecting(true);
    try {
      const res = await apiClient.rejectCandidate(rejectTarget._id, rejectReason);
      setSuccess(res.message || 'Candidate rejected.');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (e) {
      setError(friendlyApiError(e, 'Could not reject this candidate. Please try again.'));
    }
    finally { setSelecting(false); }
  };

  /**
   * What is actually in the current selection.
   *
   * Accept and reject only mean something for a resume that has been evaluated
   * and is still pending, and evaluate only means something for one that has
   * not. Showing every action regardless invites clicks that can only fail.
   */
  const selection = useMemo(() => {
    const picked = resumes.filter(r => selectedIds.has(r._id));
    const evaluable = picked.filter(r => isAwaitingEvaluation(r) || r.status === 'failed');
    const decidable = picked.filter(r =>
      r.status === 'completed'
      && (!r.candidateStatus || r.candidateStatus === 'pending')
      && meetsPassThreshold(r, passThreshold));
    return {
      total:      picked.length,
      evaluable:  evaluable.length,
      decidable:  decidable.length,
      processing: picked.filter(r => !TERMINAL_STATUSES.has(r.status) && !isAwaitingEvaluation(r)).length,
      belowBar:   picked.filter(r => r.status === 'completed' && !meetsPassThreshold(r, passThreshold)).length,
      reviewed:   picked.filter(r => r.candidateStatus === 'selected' || r.candidateStatus === 'rejected').length,
    };
  }, [resumes, selectedIds, passThreshold]);

  const hasFilters = !!(search || filterStatus || filterDecision || filterTemplate || filterScore || filterSkill);
  const isApplicantsTab = sourceTab === 'self_applied';

  const resetFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterDecision('');
    setFilterTemplate('');
    setFilterScore('');
    setFilterSkill('');
    setPage(1);
  };

  /** Switching origin refetches from scratch, so reset anything list-scoped. */
  const switchSourceTab = (key) => {
    if (key === sourceTab) return;
    setSourceTab(key);
    resetFilters();
    setSortBy('recent');
    setSelectedIds(new Set());
    setBulkMode(false);
    setError('');
    setSuccess('');
  };

  // Open select/reject from within the detail view
  const handleDetailSelect = (r) => { setViewResume(null); setSelectTarget(r); };
  const handleDetailReject = (r) => { setViewResume(null); setRejectTarget(r); setRejectReason(''); };

  // Loading skeleton
  if (loading) return (
    <div className="space-y-3">
      <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/60" />)}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} className="h-48 animate-pulse rounded-xl bg-muted/40" />)}
      </div>
    </div>
  );

  return (
    <>
    <div className="space-y-4">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Resumes</h1>
          <p className="text-[11px] text-muted-foreground">
            {isApplicantsTab
              ? <>Candidates who applied through your public job links · Default pass threshold: <strong className="text-foreground">{passThreshold}%</strong> (a job description can set its own)</>
              : <>Upload, score and screen candidates with AI · Default pass threshold: <strong className="text-foreground">{passThreshold}%</strong> (a job description can set its own)</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
          <>
          <Button size="sm" variant="outline" className="gap-1.5"
            onClick={() => { setBulkMode(b => !b); setSelectedIds(new Set()); }}>
            <Users className="h-3.5 w-3.5" />{bulkMode ? 'Cancel' : 'Select Multiple'}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5"
            onClick={() => navigate('/admin/templates/new')}
            title="Create a new job description">
            <FileText className="h-3.5 w-3.5" />Add JD
          </Button>
          {!isApplicantsTab && (
            <Button size="sm" className="gap-1.5" onClick={() => setUploadOpen(true)}>
              <Plus className="h-4 w-4" />Upload resume
            </Button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── Origin tabs ── */}
      <div className="flex items-center gap-1 border-b border-border">
        {SOURCE_TABS.map(tab => {
          const active = sourceTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchSourceTab(tab.key)}
              className={cn(
                'relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {active && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Select Multiple toolbar ── */}
      {canManage && bulkMode && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5">
          <span className="text-xs font-semibold text-primary">{selection.total} selected</span>

          {/* What is in the selection, so it is clear which actions apply */}
          {selection.total > 0 && (
            <span className="flex flex-wrap items-center gap-1.5 text-[10px]">
              {selection.evaluable > 0 && (
                <span className="rounded-full border border-violet-500/25 bg-violet-500/10 px-1.5 py-0.5 font-medium text-violet-700 dark:text-violet-400">
                  {selection.evaluable} not evaluated
                </span>
              )}
              {selection.decidable > 0 && (
                <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 font-medium text-emerald-700 dark:text-emerald-400">
                  {selection.decidable} ready to accept
                </span>
              )}
              {selection.processing > 0 && (
                <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                  {selection.processing} evaluating
                </span>
              )}
              {selection.belowBar > 0 && (
                <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 font-medium text-amber-700 dark:text-amber-400">
                  {selection.belowBar} below the pass threshold
                </span>
              )}
              {selection.reviewed > 0 && (
                <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                  {selection.reviewed} already reviewed
                </span>
              )}
            </span>
          )}

          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={bulkSelectAll}>
            {isApplicantsTab ? 'Select all unevaluated' : 'Select all eligible'}
          </Button>

          {/* Evaluate — only when something in the selection still needs it */}
          {selection.evaluable > 0 && (
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
              onClick={handleBulkEvaluate} disabled={bulkLoading}>
              {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Evaluate {selection.evaluable}
            </Button>
          )}

          {/* Accept and reject — only once something has been evaluated. A
              candidate cannot be accepted on a resume nobody has read. */}
          {selection.decidable > 0 && (
            <>
              <Button size="sm" className="h-7 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleBulkDecision('selected')} disabled={bulkLoading}>
                {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                Accept {selection.decidable}
              </Button>
              <Button size="sm" variant="destructive" className="h-7 gap-1 text-xs"
                onClick={() => handleBulkDecision('rejected')} disabled={bulkLoading}>
                {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                Reject {selection.decidable}
              </Button>
            </>
          )}

          {selection.total > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearBulkSelection}>Clear</Button>
          )}

          <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
            <Bell className="h-3 w-3" />
            {selection.total === 0
              ? `Tick candidates to evaluate or decide on them`
              : selection.evaluable > 0 && selection.decidable === 0
                ? 'Evaluate these before you can accept or reject them'
                : 'Only resumes at or above the pass threshold for their job description can be accepted'}
          </span>
        </div>
      )}

      {/* ── Alerts ── */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{error}
          <button className="ml-auto" onClick={() => setError('')}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />{success}
          <button className="ml-auto" onClick={() => setSuccess('')}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <KpiCard
          title={isApplicantsTab ? 'Applicants' : 'Total'}
          value={kpis.total} icon={FileText} tone="primary" />
        <KpiCard
          title={isApplicantsTab ? 'Not evaluated' : 'Processed'}
          value={isApplicantsTab ? kpis.awaiting : kpis.completed}
          icon={isApplicantsTab ? Sparkles : FileCheck}
          tone={isApplicantsTab ? 'primary' : 'success'} />
        <KpiCard title="Selected"  value={kpis.selected}  icon={CheckCircle2}  tone="success" />
        <KpiCard title="Rejected"  value={kpis.rejected}  icon={XCircle}       tone="destructive" />
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[10rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, email, template…" value={search}
            onChange={e => changeFilter(() => setSearch(e.target.value))}
            className="h-8 pl-7 text-xs" />
          {search && (
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => changeFilter(() => setSearch(''))}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <NativeSelect id="f-status" value={filterStatus} onChange={e => changeFilter(() => setFilterStatus(e.target.value))}
          className="w-[9rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          <option value="">All status</option>
          <option value="completed">Completed</option>
          <option value="queued">Queued</option>
          <option value="extracting">Extracting</option>
          <option value="parsing">Parsing</option>
          <option value="scoring">Scoring</option>
          <option value="failed">Failed</option>
        </NativeSelect>
        <NativeSelect id="f-decision" value={filterDecision} onChange={e => changeFilter(() => setFilterDecision(e.target.value))}
          className="w-[9rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          <option value="">All decisions</option>
          <option value="pending">Pending</option>
          <option value="selected">Selected</option>
          <option value="rejected">Rejected</option>
        </NativeSelect>

        {/* Job description — server-side, so it also shrinks the payload */}
        <NativeSelect id="f-template" value={filterTemplate}
          onChange={e => changeFilter(() => setFilterTemplate(e.target.value))}
          className="w-[13rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          <option value="">All job descriptions</option>
          {templates.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
        </NativeSelect>

        {/* ATS band */}
        <NativeSelect id="f-score" value={filterScore} onChange={e => changeFilter(() => setFilterScore(e.target.value))}
          className="w-[11rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          <option value="">Any score</option>
          {Object.entries(SCORE_BANDS).map(([k, b]) => <option key={k} value={k}>{b.label}</option>)}
        </NativeSelect>

        {/* Declared skill match — the pre-evaluation triage signal */}
        <NativeSelect id="f-skill" value={filterSkill} onChange={e => changeFilter(() => setFilterSkill(e.target.value))}
          className="w-[12rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          <option value="">Any skill match</option>
          {Object.entries(SKILL_BANDS).map(([k, b]) => <option key={k} value={k}>{b.label}</option>)}
        </NativeSelect>

        {/* Sort */}
        <NativeSelect id="f-sort" value={sortBy} onChange={e => changeFilter(() => setSortBy(e.target.value))}
          className="w-[11rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
          {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </NativeSelect>

        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={resetFilters}>
            Clear
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            {hasFilters && resumes.length !== filtered.length && (
              <span className="text-muted-foreground/60"> of {resumes.length}</span>
            )}
          </span>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {/* ── Background sync banner ── */}
      <BackgroundSyncBanner processingResumes={processingResumes} />

      {/* ── Card grid ── */}
      {paginated.length === 0 ? (
        <div className="flex h-52 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center">
          {isApplicantsTab
            ? <Inbox className="h-8 w-8 text-muted-foreground/30" />
            : <FileText className="h-8 w-8 text-muted-foreground/30" />}
          <p className="text-sm font-medium text-foreground">
            {hasFilters ? 'No resumes match your filters'
              : isApplicantsTab ? 'No applications yet'
              : 'No resumes found'}
          </p>
          {hasFilters ? (
            <Button variant="link" size="sm" className="text-xs" onClick={resetFilters}>Clear filters</Button>
          ) : isApplicantsTab ? (
            <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
              Create a public job link from a job description and share it — applications will land here automatically.
            </p>
          ) : canManage ? (
            <Button size="sm" className="mt-1 gap-1.5" onClick={() => setUploadOpen(true)}><Plus className="h-3.5 w-3.5" />Upload first resume</Button>
          ) : null}
        </div>
      ) : viewMode === 'list' ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* Column headers — hidden on mobile, where rows stack */}
          <div className="hidden grid-cols-12 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
            <span className="col-span-4">Candidate</span>
            <span className="col-span-3">Job description</span>
            <span className="col-span-2">Score</span>
            <span className="col-span-1">Decision</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {paginated.map(r => (
            <ResumeListRow key={r._id} resume={r}
              onView={handleView}
              onOpenFile={handleOpenFile}
              onSelect={r2 => setSelectTarget(r2)}
              onReject={r2 => { setRejectTarget(r2); setRejectReason(''); }}
              onDelete={r2 => setDeleteTarget(r2)}
              onEvaluate={handleEvaluate}
              evaluatingId={evaluatingId}
              selecting={selecting}
              bulkMode={bulkMode}
              selected={selectedIds.has(r._id)}
              onToggleSelect={toggleSelect}
              passThreshold={passThreshold}
              canManage={canManage}
            />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map(r => (
            <ResumeCard key={r._id} resume={r}
              onView={handleView}
              onOpenFile={handleOpenFile}
              onSelect={r2 => setSelectTarget(r2)}
              onReject={r2 => { setRejectTarget(r2); setRejectReason(''); }}
              onDelete={r2 => setDeleteTarget(r2)}
              onEvaluate={handleEvaluate}
              evaluatingId={evaluatingId}
              selecting={selecting}
              bulkMode={bulkMode}
              selected={selectedIds.has(r._id)}
              onToggleSelect={toggleSelect}
              passThreshold={passThreshold}
              canManage={canManage}
            />
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} pageSize={pageSize} onPageChange={setPage} />

      {/* ── Dialogs ── */}
      <MissingDetailsDialog
        open={Boolean(missingDetails)}
        resume={missingDetails?.resume}
        fields={missingDetails?.fields || []}
        busy={selecting}
        onCancel={() => setMissingDetails(null)}
        onSubmit={submitMissingDetails}
      />

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} templates={templates} onQueued={handleQueued} onAddJD={() => navigate('/admin/templates/new')} />

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={o => !o && setRejectTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><XCircle className="h-4 w-4" />Reject candidate</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Reject <strong className="text-foreground">{rejectTarget?.parsedData?.name || rejectTarget?.fileName}</strong>?
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason (optional)</Label>
            <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} disabled={selecting}
              placeholder="e.g. Skills don't match requirements…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring resize-none disabled:opacity-50" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRejectTarget(null)} disabled={selecting}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={confirmReject} disabled={selecting} className="gap-1.5">
              {selecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
              {selecting ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}
        title="Delete resume" description={`Remove "${deleteTarget?.fileName}"? This cannot be undone.`}
        confirmLabel="Delete" variant="destructive" onConfirm={confirmDelete} />

      <ConfirmDialog open={!!selectTarget} onOpenChange={o => !o && setSelectTarget(null)}
        title="Select candidate"
        description={
          selectTarget && !meetsPassThreshold(selectTarget, passThreshold)
            ? `This resume scored ${selectTarget.atsScore ?? 0}%, which is below the ${thresholdFor(selectTarget, passThreshold)}% pass threshold for this job description.`
            : `Create a login account for "${selectTarget?.parsedData?.name || selectTarget?.fileName}"? They scored ${selectTarget?.atsScore ?? 0}% (threshold for this role: ${thresholdFor(selectTarget, passThreshold)}%). A default password will be set.`
        }
        confirmLabel="Create account"
        loading={selecting}
        onConfirm={() => confirmSelect()}
      />

      {/* Email conflict — existing user popup */}
      <Dialog open={!!assignConflict} onOpenChange={o => !o && setAssignConflict(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />Candidate already exists in this company
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs text-muted-foreground">
            <p>
              A candidate with email <strong className="text-foreground">{assignConflict?.existingUser?.email}</strong> is already registered in this company.
            </p>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 space-y-0.5">
              <p className="font-medium text-foreground">
                {assignConflict?.existingUser?.firstName} {assignConflict?.existingUser?.lastName}
              </p>
              <p>{assignConflict?.existingUser?.email}</p>
              <p className="capitalize text-[10px]">{assignConflict?.existingUser?.role?.replace(/_/g, ' ')}</p>
            </div>
            <p>Assign this resume to the existing candidate profile, or cancel to review manually.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAssignConflict(null)} disabled={assigning}>Cancel</Button>
            <Button size="sm" onClick={confirmAssign} disabled={assigning} className="gap-1.5">
              {assigning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <User className="h-3.5 w-3.5" />}
              {assigning ? 'Assigning…' : 'Assign to this user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

    {/* ── Full-screen resume detail ── */}
    {viewResume && (
      <ResumeDetailView
        resume={viewResume}
        onClose={() => setViewResume(null)}
        onSelect={handleDetailSelect}
        onReject={handleDetailReject}
        selecting={selecting}
        passThreshold={passThreshold}
        canManage={canManage}
      />
    )}
    </>
  );
}
